/*
 * zoom.ts — Phase 14 preview zoom stops.
 *
 * Safari-style discrete zoom percentages — Cmd/Ctrl + / - / 0 in App.svelte
 * (and the command palette) walk through these stops instead of bumping the
 * preview zoom by an arbitrary delta. Discrete stops keep typography crisp
 * (no fractional `transform: scale()` rounding) and give the user a stable,
 * predictable feel that matches browsers' own zoom controls.
 *
 * `clampZoom` is the round-trip safety: any value loaded from settings or
 * passed in from a future surface (preset, share URL) snaps to the nearest
 * stop, so we never end up rendering at 87% because something old wrote it.
 *
 * The `as const` on ZOOM_STOPS gives us a literal union `ZoomStop` for free —
 * callers that need to narrow can use it, and the rest can keep treating zoom
 * as `number`.
 */
export const ZOOM_STOPS = [50, 75, 90, 100, 110, 125, 150, 175, 200, 250, 300] as const;
export type ZoomStop = (typeof ZOOM_STOPS)[number];

export function nextStop(current: number): ZoomStop {
  const i = ZOOM_STOPS.findIndex((s) => s > current);
  return (i === -1 ? ZOOM_STOPS[ZOOM_STOPS.length - 1] : ZOOM_STOPS[i]) as ZoomStop;
}

export function prevStop(current: number): ZoomStop {
  let prev: ZoomStop = ZOOM_STOPS[0];
  for (const s of ZOOM_STOPS) {
    if (s < current) prev = s;
    else break;
  }
  return prev;
}

export function clampZoom(n: number): ZoomStop {
  let best: ZoomStop = ZOOM_STOPS[0];
  let bestDiff = Infinity;
  for (const s of ZOOM_STOPS) {
    const d = Math.abs(s - n);
    if (d < bestDiff) {
      best = s;
      bestDiff = d;
    }
  }
  return best;
}
